// Post-auth data bootstrap: fetches the Dataset from the API and hydrates
// the zustand store before the app renders. Replaces the bundled seed JSON
// (moved server-side 2026-07-12 — real client data no longer ships in the
// SPA bundle).
//
// Sits inside MsalProvider/MsalAuthenticationTemplate (needs an account for
// token acquisition) and outside App (every page reads the store).

import { useCallback, useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { refreshDataset } from '@/store/refreshDataset';
import { useApiToken } from '@/auth/useApiToken';
import { api, ApiError, setTokenProvider } from '@/api/client';

// Background refresh cadence. The bootstrap below runs ONCE (`if (hydrated)
// return`) and used to be the only fetch in the app's life — see
// store/refreshDataset.ts for what that cost us.
const REFRESH_INTERVAL_MS = 120_000;
/** Don't re-fetch on a focus burst if the copy we hold is this fresh. */
const FOCUS_MIN_AGE_MS = 15_000;

function CenterScreen({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
    >
      <strong>WPI CRM</strong>
      <div>{children}</div>
    </div>
  );
}

export function DataGate({ children }: { children: React.ReactNode }) {
  const hydrated = useStore((s) => s.hydrated);
  const hydrate = useStore((s) => s.hydrate);
  const setPermissions = useStore((s) => s.setPermissions);
  const getToken = useApiToken();
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Permissions failed to resolve — admin surfaces will be missing and the
  // user needs to know that rather than concluding they lost access.
  const [permissionsFailed, setPermissionsFailed] = useState(false);

  // The store's write-through persistence needs a token outside React —
  // register the getter before any user interaction can trigger a write.
  useEffect(() => {
    setTokenProvider(getToken);
  }, [getToken]);

  useEffect(() => {
    if (hydrated) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        // /api/crm/me resolves permissions through wise-iam on every call, so
        // it fails independently of the dataset — an API restart or a wise-iam
        // blip takes it out on its own. It used to be `.catch(() => null)`,
        // which left `permissions` an EMPTY SET: every useHasPermission()
        // returned false, so the Events section, the Archive control and the
        // admin-only symposium tabs silently vanished from a fully-working
        // app, with nothing on screen to say why (hit in prod 2026-08-07,
        // right after a deploy restart). Retry once, then say so.
        const [dataset, me] = await Promise.all([
          api.getDataset(token),
          api.me(token).catch(() => api.me(token).catch(() => null)),
        ]);
        if (cancelled) return;
        if (me) setPermissions(me.permissions);
        setPermissionsFailed(!me);
        hydrate(dataset);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError({ status: err.status, message: err.message });
        else setError({ status: -1, message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, hydrated]);

  // Keep the snapshot current: poll while the tab is open, and pull
  // immediately whenever the user comes back to it (the moment they are most
  // likely to be looking for something a colleague just added).
  useEffect(() => {
    if (!hydrated) return;
    const staleEnough = () => {
      const at = useStore.getState().lastHydratedAt;
      return at === null || Date.now() - at > FOCUS_MIN_AGE_MS;
    };
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (staleEnough()) void refreshDataset();
    };
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshDataset();
    }, REFRESH_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [hydrated]);

  const permsBanner = permissionsFailed ? (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '9px 18px',
        background: '#FaF3DE', borderBottom: '1px solid #E4D2A0',
        color: '#7A5E10', fontSize: 12.5, lineHeight: 1.45,
      }}
    >
      <span>
        <b>Your permissions couldn&rsquo;t be loaded</b>, so admin-only areas (Events,
        Archive, symposium setup) are hidden. This is not a change to your access.
      </span>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginLeft: 'auto', height: 25, padding: '0 11px', flex: 'none',
          border: '1px solid currentColor', borderRadius: 4, background: 'transparent',
          color: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Reload
      </button>
    </div>
  ) : null;

  if (error) {
    const denied = error.status === 403;
    return (
      <CenterScreen>
        <span style={{ color: '#b00020', fontWeight: 700 }}>
          {denied ? 'No CRM access' : 'Could not load CRM data'}
        </span>
        <div style={{ maxWidth: 420, textAlign: 'center', fontSize: 14 }}>
          {denied
            ? 'Your account does not hold the crm.use permission. Ask an administrator to grant you a WPI CRM role in WISE-IAM.'
            : error.message}
        </div>
        {!denied && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setAttempt((a) => a + 1);
            }}
          >
            Retry
          </button>
        )}
      </CenterScreen>
    );
  }
  if (!hydrated) return <CenterScreen>Loading CRM data…</CenterScreen>;
  return <>{permsBanner}{children}</>;
}
